import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Loader2, Save, ImageIcon } from "lucide-react";
import api from "../../lib/api";
import ImageUpload from "../../components/admin/ImageUpload";
import Pagination from "../../components/admin/Pagination";

const CATEGORIES = ["rental", "excavation", "demolition", "interlock", "projects"];
const empty = { src: "", alt: { en: "", ar: "" }, cat: "projects", order: 0 };

const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/gallery");
      setItems(res.data);
    } catch { toast.error("Failed to load gallery"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId("new"); setForm({ ...empty }); };
  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({ src: item.src, alt: { en: item.alt?.en || "", ar: item.alt?.ar || "" }, cat: item.cat, order: item.order || 0 });
  };
  const close = () => { setEditingId(null); setForm(empty); };

  const save = async () => {
    if (!form.src) { toast.error("Image is required"); return; }
    setSaving(true);
    try {
      if (editingId === "new") { await api.post("/admin/gallery", form); toast.success("Added"); }
      else { await api.put(`/admin/gallery/${editingId}`, form); toast.success("Updated"); }
      await load(); close();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.cat === filter);

  useEffect(() => { setPage(1); }, [filter, pageSize]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div data-testid="admin-gallery-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Gallery</h1>
          <p className="mt-1 text-sm text-slate-500">Showcase your work — upload and organize project photos.</p>
        </div>
        <button onClick={openNew} className="admin-btn-primary" data-testid="gallery-add-btn">
          <Plus size={16} /> Add Image
        </button>
      </div>

      <div className="flex gap-1.5 mb-6 bg-slate-100 p-1 rounded-lg w-full md:w-fit overflow-x-auto admin-scroll">
        {["all", ...CATEGORIES].map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`px-3.5 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors capitalize ${
              filter === c ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => <div key={i} className="skeleton aspect-square rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <div className="inline-flex w-14 h-14 bg-slate-100 text-slate-400 rounded-full items-center justify-center mb-4"><ImageIcon size={22} /></div>
          <h3 className="font-semibold text-slate-900">No images yet</h3>
          <p className="mt-1 text-sm text-slate-500">Click "Add Image" to upload your first photo.</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {paged.map((item) => (
            <div key={item.id} className="group relative aspect-square bg-slate-100 rounded-xl overflow-hidden border border-slate-200" data-testid={`gallery-tile-${item.id}`}>
              <img src={item.src} alt={item.alt?.en} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute top-2 left-2"><span className="admin-badge admin-badge-other capitalize">{item.cat}</span></div>
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/0 to-slate-900/0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-2 gap-1.5">
                <button onClick={() => openEdit(item)} className="bg-white/90 backdrop-blur text-slate-900 p-2 rounded-lg hover:bg-white shadow-lg" data-testid={`gallery-edit-${item.id}`}><Edit2 size={13} /></button>
                <button onClick={() => remove(item.id)} className="bg-white/90 backdrop-blur text-red-600 p-2 rounded-lg hover:bg-white shadow-lg" data-testid={`gallery-delete-${item.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={filtered.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[8, 12, 16, 24]}
        />
        </>
      )}

      {editingId && (
        <div className="fixed inset-0 z-50 admin-modal-backdrop flex items-center justify-center p-3 md:p-6" onClick={close}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] shadow-2xl admin-fade-up flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3.5 border-b border-slate-200 flex justify-between items-center bg-white rounded-t-2xl shrink-0">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-600">{editingId === "new" ? "New" : "Edit"}</p>
                <h3 className="text-lg font-bold text-slate-900">Gallery Image</h3>
              </div>
              <button onClick={close} className="admin-btn-ghost p-2"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto admin-scroll flex-1">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Image <span className="text-slate-400">*</span></label>
                <ImageUpload value={form.src} onChange={(url) => setForm({ ...form, src: url })} testId="gallery-form-image" aspectClass="aspect-video w-full max-w-md" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Alt Text (EN)</label>
                  <input value={form.alt.en} onChange={(e) => setForm({ ...form, alt: { ...form.alt, en: e.target.value } })} className="admin-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Alt Text (AR)</label>
                  <input dir="rtl" value={form.alt.ar} onChange={(e) => setForm({ ...form, alt: { ...form.alt, ar: e.target.value } })} className="admin-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Category</label>
                  <select value={form.cat} onChange={(e) => setForm({ ...form, cat: e.target.value })} className="admin-input capitalize">
                    {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Display Order</label>
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="admin-input" />
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex gap-2 bg-white rounded-b-2xl shrink-0">
              <button onClick={close} className="admin-btn-secondary flex-1">Cancel</button>
              <button onClick={save} disabled={saving} className="admin-btn-primary flex-1" data-testid="gallery-form-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
